// @ts-nocheck
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import React from "react";
import StandardDetailLayout from "@/layouts/StandardDetailLayout";
import { useNavigate, useParams, useSearch } from "@tanstack/react-router";
import { useForm } from "@tanstack/react-form";
import { zodValidator } from "@tanstack/zod-form-adapter";
import { z } from "zod";
import { trpc } from "@/lib/trpc";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { FormattedNumberInput } from "@/components/shared/FormattedNumberInput";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  CardDescription,
} from "@/components/ui/card";
import {
  Save,
  Plus,
  Trash2,
  GripVertical,
  AlertCircle,
  ArrowUp,
  ArrowDown,
  Copy,
} from "lucide-react";
import { Switch } from "@/components/ui/switch";
import { joinFieldErrors } from "@/lib/form/FormFieldError";
import { toast } from "sonner";
import { ConfigCombobox } from "@/components/shared/ConfigCombobox";
import { ComboboxFK } from "@/components/shared/ComboboxFK";
import { useEntityQuickAdd } from '@/components/shared/useEntityQuickAdd';
import { useProductTypeLookup } from "@/hooks/useFKLookup";
import { QC_CLASSIFICATION_CATEGORIES } from "@/components/quality/qc-form/useQcClassification";

type Mode = "create" | "edit";

export default function QCTemplateBuilder({ mode }: { mode: Mode }) {
  const productTypeQuickAdd = useEntityQuickAdd("productType"); // thêm mới bản ghi cha theo quyền (03/11/2026)
  const navigate = useNavigate();
  const { id } = useParams({ strict: false }) as { id?: string };
  const search = useSearch({ strict: false }) as { cloneFrom?: string };
  const cloneFrom = mode === "create" ? search?.cloneFrom : undefined;
  const queryClient = useQueryClient();

  // Sửa: mẫu đang mở. Tạo phiên bản mới: chép từ mẫu nguồn (cloneFrom), lưu dạng Nháp rồi Phát hành
  const sourceId = mode === "edit" ? id : cloneFrom;
  const { data: existingTemplate, isLoading } = useQuery({
    ...trpc.qcTemplates.getById.queryOptions({ id: sourceId as string }),
    enabled: !!sourceId,
  });

  const createMutation = useMutation(trpc.qcTemplates.create.mutationOptions());
  const updateMutation = useMutation(trpc.qcTemplates.update.mutationOptions());
  const { options: productTypeOptions } = useProductTypeLookup();

  const form = useForm({
    defaultValues: {
      code: "",
      name: "",
      description: "",
      type: "IPQC",
      materialCategory: "",
      processStage: "PRINTING",
      productCategory: "HOP_MEM",
      productTypeId: "",
      samplingRule: "ISO 2859-1 Level II (AQL Major 1.5 / Minor 4.0)",
      aqlCritical: 0.0,
      aqlMajor: 1.5,
      aqlMinor: 4.0,
      isDefault: false,
      criteria: [] as any[],
      status: cloneFrom ? "DRAFT" : "ACTIVE",
      patrolFrequency: "",
      setupSampleQty: 5,
    },
    validatorAdapter: zodValidator,
    onSubmit: async ({ value }) => {
      if (!value.code?.trim() || !value.name?.trim()) {
        toast.error("Vui lòng nhập đầy đủ Mã và Tên bộ tiêu chuẩn QC");
        return;
      }
      if (!Array.isArray(value.criteria) || value.criteria.length === 0) {
        toast.error("Vui lòng thêm ít nhất 1 chỉ tiêu kiểm tra");
        return;
      }
      if (value.criteria.some((c: any) => !String(c?.name || "").trim())) {
        toast.error("Vui lòng nhập tên cho tất cả các chỉ tiêu kiểm tra");
        return;
      }
      try {
        const payload = {
          ...value,
          scope: value.type as any,
          materialType: value.type === "IQC" ? value.materialCategory || null : null,
          materialCategory: value.type === "IQC" ? value.materialCategory || null : null,
          processStage: value.type === "IPQC" ? value.processStage || null : null,
          productCategory: value.type === "FQC" ? value.productCategory || null : null,
          // Mẫu FQC gắn Loại sản phẩm thật: phiếu FQC chọn mẫu theo loại sản phẩm (trước đây không lưu → luôn rơi về mẫu FQC đầu tiên)
          productTypeId: value.type === "FQC" ? value.productTypeId || null : null,
          samplingRule: value.samplingRule || "ISO 2859-1 Level II (AQL Major 1.5 / Minor 4.0)",
          aqlCritical: Number(value.aqlCritical ?? 0),
          aqlMajor: Number(value.aqlMajor ?? 1.5),
          aqlMinor: Number(value.aqlMinor ?? 4.0),
          patrolFrequency: value.type === "IPQC" ? value.patrolFrequency || null : null,
          setupSampleQty: value.type === "IPQC" ? Number(value.setupSampleQty) || 0 : null,
          criteria: (value.criteria || []).map((c: any) => ({
            ...c,
            category: c.category || undefined,
            stage: value.type === "IPQC" ? c.stage || null : null,
            repeat: value.type === "IPQC" ? Boolean(c.repeat) : null,
          })),
        };
        if (mode === "create") {
          const res = await createMutation.mutateAsync(payload as any);
          toast.success("Đã tạo bộ tiêu chuẩn QC thành công");
          queryClient.invalidateQueries({ queryKey: trpc.qcTemplates.list.queryKey() });
          await navigate({ to: `/qc-templates/${res.id}`, replace: true });
        } else if (mode === "edit" && id) {
          const res: any = await updateMutation.mutateAsync({
            id,
            data: payload as any,
          });
          // Sửa bản đã phát hành tạo phiên bản mới (bản cũ giữ nguyên / Lưu trữ) — mở đúng phiên bản vừa lưu
          const savedId = res?.id || id;
          toast.success(
            savedId !== id
              ? `Đã tạo phiên bản v${res?.version ?? ""} ${res?.status === "ACTIVE" ? "và phát hành" : "(Nháp — bấm Phát hành để áp dụng)"}`
              : "Đã cập nhật bộ tiêu chuẩn QC",
          );
          queryClient.invalidateQueries({ queryKey: trpc.qcTemplates.list.queryKey() });
          queryClient.invalidateQueries({
            queryKey: trpc.qcTemplates.getById.queryKey({ id: savedId }),
          });
          await navigate({ to: `/qc-templates/${savedId}`, replace: true });
        }
      } catch (error: any) {
        toast.error(error?.message || "Lỗi khi lưu biểu mẫu QC");
      }
    },
  });

  React.useEffect(() => {
    if (existingTemplate && (mode === "edit" || cloneFrom)) {
      form.setFieldValue("code", existingTemplate.code);
      form.setFieldValue("name", existingTemplate.name);
      form.setFieldValue("description", existingTemplate.description || "");
      form.setFieldValue("type", existingTemplate.type || existingTemplate.scope || "IPQC");
      form.setFieldValue("materialCategory", existingTemplate.materialCategory || existingTemplate.materialType || "");
      form.setFieldValue("processStage", existingTemplate.processStage || "");
      form.setFieldValue("productCategory", existingTemplate.productCategory || "HOP_MEM");
      form.setFieldValue("productTypeId", existingTemplate.productTypeId || "");
      form.setFieldValue("samplingRule", existingTemplate.samplingRule || "ISO 2859-1 Level II (AQL Major 1.5 / Minor 4.0)");
      form.setFieldValue("aqlCritical", Number(existingTemplate.aqlCritical ?? 0));
      form.setFieldValue("aqlMajor", Number(existingTemplate.aqlMajor ?? 1.5));
      form.setFieldValue("aqlMinor", Number(existingTemplate.aqlMinor ?? 4.0));
      form.setFieldValue("isDefault", Boolean(existingTemplate.isDefault));
      form.setFieldValue(
        "criteria",
        Array.isArray(existingTemplate.criteria)
          ? existingTemplate.criteria
          : Array.isArray(existingTemplate.schema?.fields)
            ? existingTemplate.schema.fields
            : []
      );
      form.setFieldValue("status", (cloneFrom ? "DRAFT" : existingTemplate.status || "ACTIVE") as any);
      form.setFieldValue("patrolFrequency", existingTemplate.patrolFrequency || "");
      form.setFieldValue("setupSampleQty", Number(existingTemplate.setupSampleQty ?? 5));
    }
  }, [existingTemplate, mode, form, cloneFrom]);

  if (sourceId && isLoading)
    return <div className="p-8 text-center">Đang tải bộ tiêu chuẩn QC...</div>;

  const actions = (
    <div className="flex items-center gap-2">
      <Button
        type="button"
        variant="outline"
        size="sm"
        onClick={() =>
          navigate({ to: mode === "edit" && id ? `/qc-templates/${id}` : "/qc-templates" })
        }
      >
        Hủy
      </Button>
      <form.Subscribe
        selector={(state) => [state.canSubmit, state.isSubmitting]}
        children={([canSubmit, isSubmitting]) => (
          <Button
            size="sm"
            onClick={() => form.handleSubmit()}
            disabled={!canSubmit || isSubmitting}
            className="bg-indigo-600 hover:bg-indigo-700 text-white font-medium shadow-sm"
          >
            <Save className="mr-1.5 h-4 w-4" />
            {isSubmitting ? "Đang lưu..." : "Lưu bộ tiêu chuẩn"}
          </Button>
        )}
      />
    </div>
  );

  return (
    <StandardDetailLayout
      title={cloneFrom ? `Phiên bản mới v${(existingTemplate?.version || 0) + 1}` : mode === "create" ? "Tạo mẫu kiểm tra" : "Sửa mẫu kiểm tra"}
      breadcrumbs={[
        { label: "Biểu mẫu QC", to: "/qc-templates" },
        { label: mode === "create" ? "Tạo mới" : "Chỉnh sửa" },
      ]}
      actions={actions}
    >
      <div className="flex-1 overflow-auto bg-slate-50 p-6">
        <datalist id="qc-criteria-groups">
          {["Người", "Máy", "Vật tư", "Phương pháp", "Sản phẩm", "Ngoại quan", "Kích thước", "Cơ lý", "Kết cấu", "Mã vạch", "Đóng gói", "Hồ sơ"].map((g) => (
            <option key={g} value={g} />
          ))}
        </datalist>
        <div className="mx-auto max-w-5xl space-y-6">
          <Card>
            <CardHeader className="bg-white pb-4">
              <CardTitle className="text-base">Thông tin mẫu</CardTitle>
              <CardDescription>Phiếu mới tự nạp mẫu Đang áp dụng theo phân hệ và nhóm.</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4 pt-4">
              <div className="grid grid-cols-1 md:grid-cols-12 gap-4">
                <div className="md:col-span-4">
                  <form.Field
                    name="code"
                    validators={{
                      onChange: z.string().min(1, "Mã không được để trống"),
                      onSubmit: z.string().min(1, "Mã không được để trống"),
                    }}
                    children={(field) => (
                      <div className="space-y-1.5">
                        <Label htmlFor={field.name}>Mã *</Label>
                        <Input
                          id={field.name}
                          value={field.state.value}
                          onBlur={field.handleBlur}
                          onChange={(e) => field.handleChange(e.target.value)}
                          placeholder="VD: IQC-PAPER-01, IPQC-PRINTING-01"
                          className="font-mono font-bold"
                        />
                        {field.state.meta.errors ? (
                          <p className="text-xs text-red-500">
                            {joinFieldErrors(field.state.meta.errors)}
                          </p>
                        ) : null}
                      </div>
                    )}
                  />
                </div>
                <div className="md:col-span-8">
                  <form.Field
                    name="name"
                    validators={{
                      onChange: z.string().min(1, "Tên không được để trống"),
                      onSubmit: z.string().min(1, "Tên không được để trống"),
                    }}
                    children={(field) => (
                      <div className="space-y-1.5">
                        <Label htmlFor={field.name}>Tên *</Label>
                        <Input
                          id={field.name}
                          value={field.state.value}
                          onBlur={field.handleBlur}
                          onChange={(e) => field.handleChange(e.target.value)}
                          placeholder="VD: Kiểm tra giấy in"
                        />
                        {field.state.meta.errors ? (
                          <p className="text-xs text-red-500">
                            {joinFieldErrors(field.state.meta.errors)}
                          </p>
                        ) : null}
                      </div>
                    )}
                  />
                </div>
              </div>

              <form.Subscribe
                selector={(s) => s.values.type}
                children={(currentType) => (
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    <form.Field
                      name="type"
                      children={(field) => (
                        <div className="space-y-1.5">
                          <Label>Phân hệ *</Label>
                          <Select value={field.state.value} onValueChange={field.handleChange}>
                            <SelectTrigger>
                              <SelectValue placeholder="Chọn phân hệ QC" />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value="IQC">IQC · Vật tư đầu vào</SelectItem>
                              <SelectItem value="IPQC">IPQC · Công đoạn</SelectItem>
                              <SelectItem value="FQC">FQC · Thành phẩm</SelectItem>
                            </SelectContent>
                          </Select>
                        </div>
                      )}
                    />

                    {currentType === "IQC" && (
                      <form.Field
                        name="materialCategory"
                        children={(field) => (
                          <div className="space-y-1.5">
                            <Label>Nhóm vật tư</Label>
                            <ConfigCombobox
                              categoryCode={QC_CLASSIFICATION_CATEGORIES.materialGroups}
                              valueField="code"
                              canAdd
                              hideLabel
                              value={field.state.value || ""}
                              onChange={(v) => field.handleChange(String(v || "").toUpperCase())}
                              placeholder="Chọn nhóm vật tư"
                            />
                          </div>
                        )}
                      />
                    )}

                    {currentType === "IPQC" && (
                      <form.Field
                        name="processStage"
                        children={(field) => (
                          <div className="space-y-1.5">
                            <Label>Công đoạn</Label>
                            <ConfigCombobox
                              categoryCode={QC_CLASSIFICATION_CATEGORIES.processStages}
                              valueField="code"
                              canAdd
                              hideLabel
                              value={field.state.value || ""}
                              onChange={(v) => field.handleChange(String(v || "").toUpperCase())}
                              placeholder="Chọn công đoạn"
                            />
                          </div>
                        )}
                      />
                    )}

                    {currentType === "FQC" && (
                      <form.Field
                        name="productCategory"
                        children={(field) => (
                          <div className="space-y-1.5">
                            <Label>Nhóm thành phẩm</Label>
                            <ConfigCombobox
                              categoryCode={QC_CLASSIFICATION_CATEGORIES.productGroups}
                              valueField="code"
                              canAdd
                              hideLabel
                              value={field.state.value || ""}
                              onChange={(v) => field.handleChange(String(v || "").toUpperCase())}
                              placeholder="Chọn nhóm thành phẩm"
                            />
                          </div>
                        )}
                      />
                    )}

                    {currentType === "FQC" && (
                      <form.Field
                        name="productTypeId"
                        children={(field) => (
                          <div className="space-y-1.5">
                            <Label>Loại sản phẩm áp dụng</Label>
                            <ComboboxFK {...productTypeQuickAdd}
                              hideLabel
                              value={field.state.value || ""}
                              onChange={(v) => field.handleChange(v || "")}
                              options={productTypeOptions}
                              placeholder="Mọi loại trong nhóm"
                            />
                          </div>
                        )}
                      />
                    )}

                    <form.Field
                      name="status"
                      children={(field) => (
                        <div className="space-y-1.5">
                          <Label>Trạng thái</Label>
                          <Select value={field.state.value} onValueChange={field.handleChange}>
                            <SelectTrigger>
                              <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value="ACTIVE">Đang áp dụng</SelectItem>
                              <SelectItem value="DRAFT">Nháp</SelectItem>
                              <SelectItem value="ARCHIVED">Lưu trữ</SelectItem>
                            </SelectContent>
                          </Select>
                        </div>
                      )}
                    />
                  </div>
                )}
              />

              <form.Subscribe
                selector={(s) => s.values.type}
                children={(currentType) =>
                  currentType === "IPQC" ? (
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                      <form.Field
                        name="patrolFrequency"
                        children={(field) => (
                          <div className="space-y-1.5">
                            <Label htmlFor="tpl-patrol-frequency">Tần suất kiểm tra lặp lại</Label>
                            <Input
                              id="tpl-patrol-frequency"
                              value={field.state.value || ""}
                              onChange={(e) => field.handleChange(e.target.value)}
                              placeholder="VD: Mỗi 500 tờ / 30 phút"
                            />
                          </div>
                        )}
                      />
                      <form.Field
                        name="setupSampleQty"
                        children={(field) => (
                          <div className="space-y-1.5">
                            <Label htmlFor="tpl-setup-sample">Số mẫu ký</Label>
                            <Input
                              id="tpl-setup-sample"
                              type="number"
                              min={0}
                              value={field.state.value ?? 5}
                              onChange={(e) => field.handleChange(Math.max(0, Number(e.target.value) || 0))}
                            />
                          </div>
                        )}
                      />
                    </div>
                  ) : null
                }
              />

              {/* Lấy mẫu & AQL (ISO 2859-1): trước đây chỉ có giá trị mặc định, không sửa được */}
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                <form.Field
                  name="samplingRule"
                  children={(field) => (
                    <div className="space-y-1.5 col-span-2 md:col-span-1">
                      <Label htmlFor="tpl-sampling">Quy tắc lấy mẫu</Label>
                      <Input id="tpl-sampling" value={field.state.value || ""} onChange={(e) => field.handleChange(e.target.value)} placeholder="ISO 2859-1 Level II" />
                    </div>
                  )}
                />
                {([
                  ["aqlCritical", "AQL nghiêm trọng"],
                  ["aqlMajor", "AQL nặng"],
                  ["aqlMinor", "AQL nhẹ"],
                ] as const).map(([name, label]) => (
                  <form.Field
                    key={name}
                    name={name}
                    children={(field) => (
                      <div className="space-y-1.5">
                        <Label htmlFor={`tpl-${name}`}>{label}</Label>
                        <Input
                          id={`tpl-${name}`}
                          type="number"
                          step="0.1"
                          min={0}
                          value={field.state.value ?? 0}
                          onChange={(e) => field.handleChange(Math.max(0, Number(e.target.value) || 0))}
                        />
                      </div>
                    )}
                  />
                ))}
              </div>

              <form.Field
                name="description"
                children={(field) => (
                  <div className="space-y-1.5">
                    <Label htmlFor={field.name}>Mô tả</Label>
                    <Textarea
                      id={field.name}
                      value={field.state.value}
                      onBlur={field.handleBlur}
                      onChange={(e) => field.handleChange(e.target.value)}
                      placeholder="Phạm vi, cách lấy mẫu..."
                      rows={2}
                    />
                  </div>
                )}
              />
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="bg-white flex flex-row items-center justify-between pb-4">
              <div>
                <CardTitle className="text-base">Chỉ tiêu kiểm tra</CardTitle>
                <CardDescription>Định lượng có Min / Max tự đánh giá Đạt / Không đạt khi nhập số đo.</CardDescription>
              </div>
              <form.Field
                name="criteria"
                children={(field) => (
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    className="border-indigo-200 text-indigo-700 hover:bg-indigo-50 font-semibold"
                    onClick={() => {
                      field.pushValue({
                        id: `crit_${Date.now()}`,
                        name: "",
                        type: "QUANTITATIVE",
                        verificationSource: "SHOPFLOOR_MEASURED",
                        method: "",
                        standard: "",
                        defectType: "MAJOR",
                        required: true,
                        weight: 1,
                      });
                    }}
                  >
                    <Plus className="mr-1.5 h-4 w-4" /> Thêm chỉ tiêu
                  </Button>
                )}
              />
            </CardHeader>
            <CardContent className="p-0 bg-slate-50/50">
              <form.Field
                name="criteria"
                mode="array"
                children={(field) => {
                  return (
                    <div className="divide-y divide-slate-200">
                      {field.state.value.length === 0 ? (
                        <div className="p-12 text-center text-slate-500 flex flex-col items-center">
                          <AlertCircle className="h-12 w-12 text-slate-300 mb-3" />
                          <p>Chưa có chỉ tiêu nào. Bấm "Thêm chỉ tiêu" để bắt đầu.</p>
                        </div>
                      ) : (
                        field.state.value.map((_, i) => (
                          <div
                            key={i}
                            className="p-5 bg-white relative group transition-colors hover:bg-slate-50"
                          >
                            <div className="absolute right-4 top-4 flex items-center gap-1">
                              <Button
                                type="button"
                                variant="ghost"
                                size="sm"
                                title="Di chuyển lên"
                                disabled={i === 0}
                                className="h-7 w-7 p-0 text-slate-400 hover:text-indigo-600 disabled:opacity-30"
                                onClick={() => {
                                  if (i === 0) return;
                                  const next = [...field.state.value];
                                  const temp = next[i - 1];
                                  next[i - 1] = next[i];
                                  next[i] = temp;
                                  field.handleChange(next);
                                }}
                              >
                                <ArrowUp className="h-3.5 w-3.5" />
                              </Button>
                              <Button
                                type="button"
                                variant="ghost"
                                size="sm"
                                title="Di chuyển xuống"
                                disabled={i === field.state.value.length - 1}
                                className="h-7 w-7 p-0 text-slate-400 hover:text-indigo-600 disabled:opacity-30"
                                onClick={() => {
                                  if (i >= field.state.value.length - 1) return;
                                  const next = [...field.state.value];
                                  const temp = next[i + 1];
                                  next[i + 1] = next[i];
                                  next[i] = temp;
                                  field.handleChange(next);
                                }}
                              >
                                <ArrowDown className="h-3.5 w-3.5" />
                              </Button>
                              <Button
                                type="button"
                                variant="ghost"
                                size="sm"
                                title="Nhân bản chỉ tiêu này"
                                className="h-7 w-7 p-0 text-slate-400 hover:text-indigo-600"
                                onClick={() => {
                                  const src = field.state.value[i];
                                  const clone = {
                                    ...src,
                                    id: `crit_${Date.now()}`,
                                    name: `${src.name || "Chỉ tiêu"} (Bản sao)`,
                                  };
                                  const next = [...field.state.value];
                                  next.splice(i + 1, 0, clone);
                                  field.handleChange(next);
                                }}
                              >
                                <Copy className="h-3.5 w-3.5" />
                              </Button>
                              <Button
                                type="button"
                                variant="ghost"
                                size="sm"
                                title="Xóa chỉ tiêu"
                                className="h-7 w-7 p-0 text-slate-400 hover:text-red-600"
                                onClick={() => field.removeValue(i)}
                              >
                                <Trash2 className="h-4 w-4" />
                              </Button>
                            </div>

                            <div className="flex gap-3">
                              <div className="mt-2 text-slate-300">
                                <GripVertical className="h-5 w-5" />
                              </div>
                              <div className="flex-1 space-y-3 pr-8">
                                <div className="grid grid-cols-1 md:grid-cols-12 gap-3">
                                  <div className="md:col-span-5">
                                    <form.Field
                                      name={`criteria[${i}].name` as any}
                                      children={(subField) => (
                                        <div className="space-y-1">
                                          <Label className="text-xs font-semibold text-slate-600">
                                            Chỉ tiêu *
                                          </Label>
                                          <Input
                                            value={(subField.state.value as string) || ""}
                                            onBlur={subField.handleBlur}
                                            onChange={(e) => subField.handleChange(e.target.value)}
                                            placeholder="VD: Định lượng, Chồng màu"
                                            className="h-8 text-xs font-semibold"
                                          />
                                        </div>
                                      )}
                                    />
                                  </div>
                                  <div className="md:col-span-3">
                                    <form.Field
                                      name={`criteria[${i}].type` as any}
                                      children={(subField) => (
                                        <div className="space-y-1">
                                          <Label className="text-xs font-semibold text-slate-600">
                                            Loại
                                          </Label>
                                          <Select
                                            value={(subField.state.value as string) || "QUALITATIVE"}
                                            onValueChange={subField.handleChange}
                                          >
                                            <SelectTrigger className="h-8 text-xs">
                                              <SelectValue />
                                            </SelectTrigger>
                                            <SelectContent>
                                              <SelectItem value="QUANTITATIVE">Định lượng</SelectItem>
                                              <SelectItem value="QUALITATIVE">Định tính</SelectItem>
                                            </SelectContent>
                                          </Select>
                                        </div>
                                      )}
                                    />
                                  </div>
                                  <div className="md:col-span-2">
                                    <form.Field
                                      name={`criteria[${i}].defectType` as any}
                                      children={(subField) => (
                                        <div className="space-y-1">
                                          <Label className="text-xs font-semibold text-slate-600">
                                            Mức lỗi
                                          </Label>
                                          <Select
                                            value={(subField.state.value as string) || "MAJOR"}
                                            onValueChange={subField.handleChange}
                                          >
                                            <SelectTrigger className="h-8 text-xs">
                                              <SelectValue />
                                            </SelectTrigger>
                                            <SelectContent>
                                              <SelectItem value="CRITICAL">Nghiêm trọng</SelectItem>
                                              <SelectItem value="MAJOR">Nặng</SelectItem>
                                              <SelectItem value="MINOR">Nhẹ</SelectItem>
                                            </SelectContent>
                                          </Select>
                                        </div>
                                      )}
                                    />
                                  </div>
                                  <div className="md:col-span-2 flex items-center justify-end pt-5">
                                    <form.Field
                                      name={`criteria[${i}].required` as any}
                                      children={(subField) => (
                                        <div className="flex items-center gap-2">
                                          <Switch
                                            checked={subField.state.value !== false}
                                            onCheckedChange={subField.handleChange}
                                          />
                                          <Label className="text-xs font-medium">Bắt buộc</Label>
                                        </div>
                                      )}
                                    />
                                  </div>
                                </div>

                                <div className="grid grid-cols-1 md:grid-cols-12 gap-3">
                                  <div className="md:col-span-4">
                                    <form.Field
                                      name={`criteria[${i}].verificationSource` as any}
                                      children={(subField) => (
                                        <div className="space-y-1">
                                          <Label className="text-xs font-semibold text-indigo-700">
                                            Nguồn
                                          </Label>
                                          <Select
                                            value={(subField.state.value as string) || "SHOPFLOOR_MEASURED"}
                                            onValueChange={subField.handleChange}
                                          >
                                            <SelectTrigger className="h-8 text-xs border-indigo-200 bg-indigo-50/30">
                                              <SelectValue />
                                            </SelectTrigger>
                                            <SelectContent>
                                              <SelectItem value="SHOPFLOOR_MEASURED">Đo tại xưởng</SelectItem>
                                              <SelectItem value="SUPPLIER_COA">Theo CoA</SelectItem>
                                              <SelectItem value="EXTERNAL_LAB">Gửi lab</SelectItem>
                                            </SelectContent>
                                          </Select>
                                        </div>
                                      )}
                                    />
                                  </div>
                                  <div className="md:col-span-4">
                                    <form.Field
                                      name={`criteria[${i}].method` as any}
                                      children={(subField) => (
                                        <div className="space-y-1">
                                          <Label className="text-xs text-slate-600">
                                            Dụng cụ
                                          </Label>
                                          <Input
                                            value={(subField.state.value as string) || ""}
                                            onChange={(e) => subField.handleChange(e.target.value)}
                                            placeholder="VD: Thước cặp, cân + dao 100 cm²"
                                            className="h-8 text-xs"
                                          />
                                        </div>
                                      )}
                                    />
                                  </div>
                                  <div className="md:col-span-4">
                                    <form.Field
                                      name={`criteria[${i}].standard` as any}
                                      children={(subField) => (
                                        <div className="space-y-1">
                                          <Label className="text-xs text-slate-600">
                                            Tiêu chuẩn
                                          </Label>
                                          <Input
                                            value={(subField.state.value as string) || ""}
                                            onChange={(e) => subField.handleChange(e.target.value)}
                                            placeholder="VD: Đúng sớ đặt hàng"
                                            className="h-8 text-xs"
                                          />
                                        </div>
                                      )}
                                    />
                                  </div>
                                </div>

                                <div className="grid grid-cols-1 md:grid-cols-12 gap-3">
                                  <div className="md:col-span-4">
                                    <form.Field
                                      name={`criteria[${i}].category` as any}
                                      children={(subField) => (
                                        <div className="space-y-1">
                                          <Label className="text-xs text-slate-600">Nhóm</Label>
                                          <Input
                                            list="qc-criteria-groups"
                                            value={(subField.state.value as string) || ""}
                                            onChange={(e) => subField.handleChange(e.target.value)}
                                            placeholder="VD: Người, Máy, Sản phẩm"
                                            className="h-8 text-xs"
                                          />
                                        </div>
                                      )}
                                    />
                                  </div>
                                  <form.Subscribe
                                    selector={(st) => st.values.type}
                                    children={(currentType) =>
                                      currentType === "IPQC" ? (
                                        <>
                                          <div className="md:col-span-4">
                                            <form.Field
                                              name={`criteria[${i}].stage` as any}
                                              children={(subField) => (
                                                <div className="space-y-1">
                                                  <Label className="text-xs text-slate-600">Giai đoạn</Label>
                                                  <Select value={(subField.state.value as string) || "BOTH"} onValueChange={subField.handleChange}>
                                                    <SelectTrigger className="h-8 text-xs">
                                                      <SelectValue />
                                                    </SelectTrigger>
                                                    <SelectContent>
                                                      <SelectItem value="SETUP">Duyệt setup</SelectItem>
                                                      <SelectItem value="PATROL">Lặp lại</SelectItem>
                                                      <SelectItem value="BOTH">Cả hai</SelectItem>
                                                    </SelectContent>
                                                  </Select>
                                                </div>
                                              )}
                                            />
                                          </div>
                                          <div className="md:col-span-4 flex items-center pt-5">
                                            <form.Field
                                              name={`criteria[${i}].repeat` as any}
                                              children={(subField) => (
                                                <div className="flex items-center gap-2">
                                                  <Switch checked={Boolean(subField.state.value)} onCheckedChange={subField.handleChange} />
                                                  <Label className="text-xs font-medium">Đo lại mỗi lần kiểm</Label>
                                                </div>
                                              )}
                                            />
                                          </div>
                                        </>
                                      ) : null
                                    }
                                  />
                                </div>

                                {field.state.value[i]?.type === "QUANTITATIVE" && (
                                  <div className="rounded-lg border border-slate-200 bg-slate-50 p-3 grid grid-cols-2 md:grid-cols-4 gap-3">
                                    <form.Field
                                      name={`criteria[${i}].targetValue` as any}
                                      children={(subField) => (
                                        <div className="space-y-1">
                                          <Label className="text-xs text-slate-600">
                                            Chuẩn
                                          </Label>
                                          <FormattedNumberInput
                                            value={subField.state.value as number}
                                            onValueChange={(val) => subField.handleChange(val)}
                                            placeholder="VD: 300"
                                          />
                                        </div>
                                      )}
                                    />
                                    <form.Field
                                      name={`criteria[${i}].minValue` as any}
                                      children={(subField) => (
                                        <div className="space-y-1">
                                          <Label className="text-xs text-slate-600">
                                            Min
                                          </Label>
                                          <FormattedNumberInput
                                            value={subField.state.value as number}
                                            onValueChange={(val) => subField.handleChange(val)}
                                            placeholder="VD: 285"
                                          />
                                        </div>
                                      )}
                                    />
                                    <form.Field
                                      name={`criteria[${i}].maxValue` as any}
                                      children={(subField) => (
                                        <div className="space-y-1">
                                          <Label className="text-xs text-slate-600">
                                            Max
                                          </Label>
                                          <FormattedNumberInput
                                            value={subField.state.value as number}
                                            onValueChange={(val) => subField.handleChange(val)}
                                            placeholder="VD: 315"
                                          />
                                        </div>
                                      )}
                                    />
                                    <form.Field
                                      name={`criteria[${i}].unit` as any}
                                      children={(subField) => (
                                        <div className="space-y-1">
                                          <Label className="text-xs text-slate-600">
                                            ĐVT
                                          </Label>
                                          <Input
                                            value={(subField.state.value as string) || ""}
                                            onChange={(e) => subField.handleChange(e.target.value)}
                                            placeholder="VD: g/m², mm, %, ΔE"
                                            className="h-9 text-xs font-mono"
                                          />
                                        </div>
                                      )}
                                    />
                                  </div>
                                )}
                              </div>
                            </div>
                          </div>
                        ))
                      )}
                    </div>
                  );
                }}
              />
            </CardContent>
          </Card>

          <div className="flex items-center justify-end gap-2 pt-2 pb-6">
            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={() =>
                navigate({ to: mode === "edit" && id ? `/qc-templates/${id}` : "/qc-templates" })
              }
            >
              Hủy
            </Button>
            <form.Subscribe
              selector={(state) => [state.canSubmit, state.isSubmitting]}
              children={([canSubmit, isSubmitting]) => (
                <Button
                  type="button"
                  size="sm"
                  onClick={() => form.handleSubmit()}
                  disabled={!canSubmit || isSubmitting}
                  className="bg-indigo-600 hover:bg-indigo-700 text-white font-medium shadow-sm"
                >
                  <Save className="mr-1.5 h-4 w-4" />
                  {isSubmitting ? "Đang lưu..." : "Lưu bộ tiêu chuẩn"}
                </Button>
              )}
            />
          </div>
        </div>
      </div>
    </StandardDetailLayout>
  );
}
